import { cn } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"
import { AlertCircle, ArrowLeft, RefreshCw } from "lucide-react"

interface ErrorStateProps {
	/** Main heading text */
	title?: string
	/** Description text */
	description?: string
	/** Optional retry callback */
	onRetry?: () => void
	/** Whether retry is in progress */
	isRetrying?: boolean
	/** Optional back link */
	backHref?: string
	/** Back link label */
	backLabel?: string
	/** Additional className for the container */
	className?: string
	/** Icon to display */
	icon?: LucideIcon
	/** Variant style */
	variant?: "default" | "inline" | "full"
}

/**
 * Standardized error state component.
 *
 * Displays an error message with optional retry and back actions.
 * Used when data fails to load or an operation fails.
 *
 * @example
 * // Simple inline error
 * <ErrorState variant="inline" title="Failed to load activity" />
 *
 * // Error with retry
 * <ErrorState
 *   title="Failed to load positions"
 *   description="Please try again"
 *   onRetry={() => refetch()}
 * />
 *
 * // Full page error
 * <ErrorState
 *   variant="full"
 *   title="Page Not Found"
 *   description="The page you're looking for doesn't exist."
 *   backHref="/"
 *   backLabel="Back to home"
 * />
 */
export function ErrorState({
	title = "Something went wrong",
	description = "Please try again or go back to the previous page.",
	onRetry,
	isRetrying = false,
	backHref = "/",
	backLabel = "Go back",
	className,
	icon: Icon = AlertCircle,
	variant = "default",
}: ErrorStateProps) {
	// Inline variant - simple text-only error
	if (variant === "inline") {
		return (
			<div
				className={cn(
					"rounded-2xl border border-border bg-bg-card p-6 shadow-sm dark:border-border-subtle",
					className,
				)}
			>
				<p className="text-center text-text-muted">{title}</p>
				{onRetry && (
					<button
						className="mx-auto mt-3 flex items-center gap-2 font-medium text-sm text-text-secondary hover:text-text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 disabled:opacity-50"
						disabled={isRetrying}
						onClick={onRetry}
						type="button"
					>
						<RefreshCw aria-hidden="true" className={cn("size-4", isRetrying && "animate-spin")} />
						{isRetrying ? "Retrying…" : "Try again"}
					</button>
				)}
			</div>
		)
	}

	// Full variant - centered page-level error
	if (variant === "full") {
		return (
			<div
				className={cn(
					"mx-auto max-w-md rounded-2xl border border-border bg-bg-card p-8 text-center shadow-sm dark:border-border-subtle",
					className,
				)}
			>
				<div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
					<Icon aria-hidden="true" className="size-8 text-red-500 dark:text-red-400" />
				</div>
				<h2 className="text-balance font-semibold text-lg text-text-primary">{title}</h2>
				{description && <p className="mt-2 text-pretty text-base text-text-muted">{description}</p>}
				<div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
					{onRetry && (
						<button
							className="inline-flex items-center gap-2 rounded-full border border-text-primary bg-text-primary px-4 py-2 font-semibold text-bg-primary transition-colors hover:opacity-90 disabled:opacity-50"
							disabled={isRetrying}
							onClick={onRetry}
							type="button"
						>
							<RefreshCw
								aria-hidden="true"
								className={cn("size-4", isRetrying && "animate-spin")}
							/>
							{isRetrying ? "Retrying…" : "Try again"}
						</button>
					)}
					{backHref && (
						<Link
							className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-card px-4 py-2 font-semibold text-text-secondary transition-colors hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 dark:border-border-subtle"
							to={backHref}
						>
							<ArrowLeft aria-hidden="true" className="size-4" />
							{backLabel}
						</Link>
					)}
				</div>
			</div>
		)
	}

	// Default variant - card with icon and actions
	return (
		<div
			className={cn(
				"rounded-2xl border border-border bg-bg-card p-10 text-center shadow-sm dark:border-border-subtle",
				className,
			)}
		>
			<div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
				<Icon aria-hidden="true" className="size-8 text-red-500 dark:text-red-400" />
			</div>
			<h3 className="mb-2 text-balance font-display font-semibold text-lg text-text-primary">
				{title}
			</h3>
			{description && <p className="text-pretty text-base text-text-muted">{description}</p>}
			{(onRetry || backHref) && (
				<div className="mt-4 flex flex-col items-center gap-2">
					{onRetry && (
						<button
							className="inline-flex items-center gap-2 font-medium text-text-secondary hover:text-text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 disabled:opacity-50"
							disabled={isRetrying}
							onClick={onRetry}
							type="button"
						>
							<RefreshCw
								aria-hidden="true"
								className={cn("size-4", isRetrying && "animate-spin")}
							/>
							{isRetrying ? "Retrying…" : "Try again"}
						</button>
					)}
					{backHref && (
						<Link
							className="font-medium text-text-muted hover:text-text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2"
							to={backHref}
						>
							{backLabel}
						</Link>
					)}
				</div>
			)}
		</div>
	)
}
